import { useState } from 'react';

import {
  AdornedInput,
  Box,
  Button,
  ButtonGroup,
  FormField,
  FormHelp,
  Heading,
  Label,
  Modal,
  TextButton,
} from '@zillow/constellation-10';

import { useQueryClient } from '@zg-rentals/react-query5';
import { FeeType } from '@zg-rentals/ts-rental-manager-api-beta-client';
import {
  useRentalManagerUserPropertyDetailsWebApiHandler_overview,
  useRentalManagerUserPropertyEditWebApiHandler_isApplicationsAccepted,
  useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_getFeesForType,
  useRentalManagerUserPropertySummaryWebApiHandler_nextSteps,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import propertyUtils from '../../../utils/propertyUtils';

import {
  emitApplicationsDisableFromEnabledEvent,
  emitApplicationsEnableFromDisabledEvent,
} from '../../../analytics/events/modularListing';
import { useAuth } from '../../AuthProvider';
import ApplicationsFormField from '../../PostingPath/ApplicationsFormField';

import type { ChangeEvent } from 'react';

type Props = {
  listingAlias: string;
  isFeed?: boolean;
  isIntroModal?: boolean;
  onClose: () => void;
};

const ApplicationsModal = ({ listingAlias, isFeed = false, isIntroModal = false, onClose }: Props) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const { data: overviewData } = useRentalManagerUserPropertyDetailsWebApiHandler_overview({ listingAlias });

  const isMultifamily = overviewData?.isMultifamily ?? false;
  const initialIsApplicationsAccepted = overviewData?.isApplicationsAccepted;
  const isRoomForRent = propertyUtils.isRoomForRent({ listing: overviewData });

  const canAccessSFCostsAndFees = !isMultifamily && !isFeed;

  const [radioValue, setRadioValue] = useState<string>(
    initialIsApplicationsAccepted === null || initialIsApplicationsAccepted === undefined
      ? ''
      : String(initialIsApplicationsAccepted),
  );
  const [feeAmount, setFeeAmount] = useState<string>('');
  const [feeError, setFeeError] = useState<string>('');
  const [serverError, setServerError] = useState<string>('');

  const { data: applicationFee = [] } = useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_getFeesForType(
    { feeType: FeeType.APPLICATION_FEE, listingAlias },
    { enabled: canAccessSFCostsAndFees && Boolean(listingAlias) },
  );

  // Initialise fee amount from query data once loaded
  const initialFeeAmount = applicationFee?.length > 0 ? String(applicationFee[0].feeAmountMin ?? '') : '';

  const { mutate: editIsApplicationsAccepted, isPending: isSaving } =
    useRentalManagerUserPropertyEditWebApiHandler_isApplicationsAccepted({
      meta: {
        context: {
          api: 'rental-manager',
          details: 'useRentalManagerUserPropertyEditWebApiHandler_isApplicationsAccepted',
        },
        invalidates: ['users-properties-webAnalytics-get'],
      },
    });

  const validateFeeAmount = (value: string): string => {
    if (!value && value !== '0') return 'Amount cannot be blank.';
    const num = Number(value);
    if (Number.isNaN(num) || num < 0) return 'Amount must be a non-negative number.';
    return '';
  };

  const save = (newRadioValue: string, newFeeAmount: string) => {
    if (canAccessSFCostsAndFees && !isMultifamily && newRadioValue === 'false') {
      const err = validateFeeAmount(newFeeAmount);
      if (err) {
        setFeeError(err);
        return;
      }
    }

    setServerError('');

    const prevIsApplicationsAccepted = initialIsApplicationsAccepted;
    const nextValue = newRadioValue === 'true';

    const params: {
      propertyId: string;
      isApplicationsAccepted: boolean;
      inviteOnly: boolean;
      applicationFeeAmount?: number;
    } = {
      propertyId: listingAlias,
      isApplicationsAccepted: nextValue,
      inviteOnly: false,
    };

    if (canAccessSFCostsAndFees && !isMultifamily && newRadioValue === 'false' && newFeeAmount !== '') {
      params.applicationFeeAmount = Number(newFeeAmount);
    }

    editIsApplicationsAccepted(
      { getParams: params },
      {
        onSuccess: () => {
          const csParams = { currentProperty: { listingAlias }, user: user ?? {} };

          if (nextValue && prevIsApplicationsAccepted === false) {
            emitApplicationsEnableFromDisabledEvent({ ...csParams, triggerLocation: 'zrm_property_overview' });
          } else if (!nextValue && prevIsApplicationsAccepted === true) {
            emitApplicationsDisableFromEnabledEvent({ ...csParams, triggerLocation: 'zrm_property_overview' });
          }

          queryClient.invalidateQueries({
            queryKey: useRentalManagerUserPropertyDetailsWebApiHandler_overview.getKey({ listingAlias }),
          });
          queryClient.invalidateQueries({
            queryKey: useRentalManagerUserPropertySummaryWebApiHandler_nextSteps.getKey({ listingAlias }),
          });
          onClose();
        },
        onError: (error) => {
          const err = error as { message?: string };
          setServerError(err?.message ?? 'Unable to update your applications setting. Try again.');
        },
      },
    );
  };

  const handleRadioChange = (event: ChangeEvent<Element>) => {
    const newValue = (event.target as HTMLInputElement).value;
    setRadioValue(newValue);
    setServerError('');
    setFeeError('');
  };

  const handleFeeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setFeeAmount(newValue);
    setFeeError('');
  };

  const currentFeeAmount = feeAmount !== '' ? feeAmount : initialFeeAmount;

  return (
    <>
      <Modal
        open={true}
        onOpenChange={(open) => !open && onClose()}
        size={{ base: 'fullScreen', md: 'md' }}
        header={<Heading level={2}>Zillow applications</Heading>}
        body={
          <Box css={{ padding: '600' }} data-testid="EnableApplicationsModal">
            <ApplicationsFormField
              isIntroModal={isIntroModal}
              isRoomForRent={isRoomForRent}
              radioValue={radioValue}
              onRadioChange={handleRadioChange}
              serverError={serverError}
              disabled={isSaving}
            />

            {canAccessSFCostsAndFees && !isMultifamily && radioValue === 'false' && (
              <Box css={{ paddingLeft: '800', marginTop: '400' }}>
                <FormField
                  label={<Label>Fee amount</Label>}
                  required={true}
                  error={Boolean(feeError)}
                  control={
                    <AdornedInput
                      css={{ maxWidth: '360px' }}
                      startAdornment={<AdornedInput.Adornment>$</AdornedInput.Adornment>}
                      input={
                        <AdornedInput.Input
                          name="applicationFeeAmount"
                          value={currentFeeAmount}
                          onChange={handleFeeChange}
                          type="number"
                          disabled={isSaving}
                        />
                      }
                      endAdornment={<AdornedInput.Adornment>/applicant</AdornedInput.Adornment>}
                    />
                  }
                  description={
                    <FormHelp>
                      {feeError ||
                        'Include the cost of the background check and credit report. Enter $0 if you won’t charge a fee.'}
                    </FormHelp>
                  }
                />
              </Box>
            )}
          </Box>
        }
        footer={
          <ButtonGroup aria-label="Actions">
            <TextButton onClick={onClose}>Cancel</TextButton>
            <Button
              tone="brand"
              emphasis="filled"
              onClick={() => save(radioValue, feeAmount || initialFeeAmount)}
              loading={isSaving}
            >
              Save
            </Button>
          </ButtonGroup>
        }
      />
    </>
  );
};

export default ApplicationsModal;
